import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ReactFlow, Background, Controls, Edge, Node, NodeProps, Position, addEdge, useEdgesState, useNodesState, ControlButton, useReactFlow, ReactFlowProvider } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Founder } from '../utils/types';
import { calculateCompatibility } from '../utils/mockData';
import { FitViewOptions } from '@xyflow/core';
import { createPortal } from 'react-dom';
interface TeamGraphProps {
  founders: Founder[];
  selectedFounder: Founder | null;
  comparedFounder: Founder | null;
  onFounderSelect: (founder: Founder, isShiftKey: boolean) => void;
}
// Custom node component
const FounderNode = ({
  data,
  selected
}: NodeProps) => {
  const founder: Founder = data.founder;
  const archetypeColors = {
    CEO: 'bg-purple-500',
    CTO: 'bg-green-500'
  };
  const color = archetypeColors[founder.archetype as keyof typeof archetypeColors];
  const size = 30 + founder.potentialScore / 5; // Size based on potential score
  return <div className={`flex flex-col items-center group ${selected ? 'ring-2 ring-white' : ''}`} style={{
    cursor: 'pointer'
  }} data-founder-id={founder.id} data-id="element-557">
      <div className={`rounded-full flex items-center justify-center ${color}`} style={{
      width: `${size}px`,
      height: `${size}px`
    }} data-id="element-558">
        {founder.photoUrl ? <img src={founder.photoUrl} alt={founder.name} className="w-full h-full rounded-full object-cover border-2 border-transparent" data-id="element-559" /> : <span className="text-xs font-bold text-white" data-id="element-560">
            {founder.name.split(' ').map(n => n[0]).join('')}
          </span>}
      </div>
    </div>;
};
// Separate tooltip component that will be rendered at the root level
const NodeTooltip = ({
  hoveredFounder,
  position
}: {
  hoveredFounder: Founder | null;
  position: {
    x: number;
    y: number;
  } | null;
}) => {
  if (!hoveredFounder || !position) return null;
  return createPortal(<div className="fixed bg-[#222] px-2 py-0.5 rounded text-xs whitespace-nowrap pointer-events-none text-white" style={{
    left: `${position.x}px`,
    top: `${position.y + 15}px`,
    zIndex: 9999
  }} data-id="element-561">
      {hoveredFounder.name}
    </div>, document.body);
};
// Export a wrapped version of TeamGraph
export const TeamGraph = ({
  founders,
  selectedFounder,
  comparedFounder,
  onFounderSelect
}: TeamGraphProps) => {
  return <ReactFlowProvider data-id="element-562">
      <TeamGraphInner founders={founders} selectedFounder={selectedFounder} comparedFounder={comparedFounder} onFounderSelect={onFounderSelect} data-id="element-563" />
    </ReactFlowProvider>;
};
// Move the main component implementation to an inner component
const TeamGraphInner = ({
  founders,
  selectedFounder,
  comparedFounder,
  onFounderSelect
}: TeamGraphProps) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [compatibilityEdges, setCompatibilityEdges] = useState<Edge[]>([]);
  const [hoveredFounder, setHoveredFounder] = useState<Founder | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const {
    fitView
  } = useReactFlow();
  // Initialize nodes in a circular layout
  useEffect(() => {
    if (founders.length) {
      const radius = Math.min(window.innerWidth, window.innerHeight) * 0.35;
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const newNodes: Node[] = founders.map((founder, index) => {
        const angle = index / founders.length * 2 * Math.PI;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);
        return {
          id: founder.id,
          position: {
            x,
            y
          },
          data: {
            founder
          },
          type: 'founderNode'
        };
      });
      setNodes(newNodes);
    }
  }, [founders, setNodes]);
  // Update edges when a founder is selected
  useEffect(() => {
    if (selectedFounder) {
      const newEdges: Edge[] = [];
      founders.forEach(founder => {
        if (founder.id !== selectedFounder.id) {
          const compatibility = calculateCompatibility(selectedFounder, founder);
          // Only show edges for good compatibility
          if (compatibility.overall > 60) {
            const edgeId = `e-${selectedFounder.id}-${founder.id}`;
            const color = getCompatibilityColor(compatibility.overall);
            newEdges.push({
              id: edgeId,
              source: selectedFounder.id,
              target: founder.id,
              style: {
                stroke: color,
                strokeWidth: Math.max(1, compatibility.overall / 25)
              },
              label: `${compatibility.overall}%`,
              labelStyle: {
                fill: color,
                fontWeight: 'bold'
              },
              labelBgStyle: {
                fill: '#111',
                fillOpacity: 0.7
              },
              animated: compatibility.overall > 85
            });
          }
        }
      });
      setCompatibilityEdges(newEdges);
      // If there's a compared founder, add a special edge
      if (comparedFounder) {
        const compatibility = calculateCompatibility(selectedFounder, comparedFounder);
        const edgeId = `e-compared-${selectedFounder.id}-${comparedFounder.id}`;
        const color = getCompatibilityColor(compatibility.overall);
        setCompatibilityEdges(prev => [...prev, {
          id: edgeId,
          source: selectedFounder.id,
          target: comparedFounder.id,
          style: {
            stroke: color,
            strokeWidth: Math.max(2, compatibility.overall / 20)
          },
          label: `${compatibility.overall}%`,
          labelStyle: {
            fill: color,
            fontWeight: 'bold'
          },
          labelBgStyle: {
            fill: '#111',
            fillOpacity: 0.7
          },
          animated: true
        }]);
      }
    } else {
      setCompatibilityEdges([]);
    }
  }, [selectedFounder, comparedFounder, founders]);
  // Handle node click
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    const founder = (node.data as any).founder as Founder;
    onFounderSelect(founder, event.shiftKey);
  }, [onFounderSelect]);
  // Handle mouse over for tooltips
  const onNodeMouseEnter = useCallback((event: React.MouseEvent, node: Node) => {
    const founder = (node.data as any).founder as Founder;
    setHoveredFounder(founder);
    // Calculate position relative to the viewport
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom
    });
  }, []);
  const onNodeMouseLeave = useCallback(() => {
    setHoveredFounder(null);
    setTooltipPosition(null);
  }, []);
  // Handle pane click to clear tooltip
  const onPaneClick = useCallback(() => {
    setHoveredFounder(null);
    setTooltipPosition(null);
  }, []);
  // Get color based on compatibility score
  const getCompatibilityColor = (score: number): string => {
    if (score >= 90) return '#10b981'; // green-500
    if (score >= 80) return '#22c55e'; // green-400
    if (score >= 70) return '#fbbf24'; // yellow-400
    if (score >= 60) return '#f97316'; // orange-500
    return '#ef4444'; // red-500
  };
  // Register custom node types
  const nodeTypes = {
    founderNode: FounderNode
  };
  // Create custom controls without zoom buttons
  const fitViewOptions: FitViewOptions = {
    padding: 0.2
  };
  return <div className="w-full h-full bg-[#0a0a0a]" ref={reactFlowWrapper} data-id="element-564">
      <ReactFlow nodes={nodes} edges={[...edges, ...compatibilityEdges]} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} nodeTypes={nodeTypes} onNodeClick={onNodeClick} onNodeMouseEnter={onNodeMouseEnter} onNodeMouseLeave={onNodeMouseLeave} onPaneClick={onPaneClick} minZoom={0.2} maxZoom={2} defaultViewport={{
      x: 0,
      y: 0,
      zoom: 1
    }} fitView fitViewOptions={fitViewOptions} proOptions={{
      hideAttribution: true
    }} data-id="element-565">
        <Background color="#222" gap={20} data-id="element-566" />
        <div className="react-flow__controls" style={{
        background: '#111',
        border: '1px solid #222'
      }} data-id="element-567">
          <ControlButton onClick={() => fitView(fitViewOptions)} title="Fit view" style={{
          background: '#111',
          border: '1px solid #222',
          color: '#fff'
        }} data-id="element-568">
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" data-id="element-569">
              <path fill="currentColor" d="M15 3l2.3 2.3-2.89 2.87 1.42 1.42L18.7 6.7 21 9V3h-6zM3 9l2.3-2.3 2.87 2.89 1.42-1.42L6.7 5.3 9 3H3v6zm6 12l-2.3-2.3 2.89-2.87-1.42-1.42L5.3 17.3 3 15v6h6zm12-6l-2.3 2.3-2.87-2.89-1.42 1.42 2.89 2.87L15 21h6v-6z" data-id="element-570" />
            </svg>
          </ControlButton>
        </div>
      </ReactFlow>
      {/* Render tooltip */}
      <NodeTooltip hoveredFounder={hoveredFounder} position={tooltipPosition} data-id="element-571" />
    </div>;
};